/* eslint-disable lit-a11y/click-events-have-key-events */
import { html, unsafeCSS } from 'lit';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';
import { property, query, state, eventOptions } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { onceTransitionEnd } from '@rcaferati/wac';
import { nanoid } from 'nanoid';
import { addons } from 'storybook/preview-api';
import { ClickAwayController, FocusTrapController } from '../../../internal';

import { BaseElement, customElement } from '../../base-element';
import '../../buttons/icon-button/icon-button.wc';
import { PortalDestinationWC } from '../../portal/portal-destination.wc';
import styles from './modal.scss?inline';

@customElement('ps-modal')
export class ModalWC extends BaseElement {
  onClickAway = new ClickAwayController({
    host: this,
  });

  focusTrapController = new FocusTrapController(this);

  static styles = unsafeCSS(styles);

  private parentEl: Element | null;

  @query('.c-modal') modal?: HTMLDialogElement;

  @query('.c-modal__body') modalBody: HTMLElement;

  private portalDestinationEl: HTMLElement | null;

  /**
   * Which `_closeModal` call may still finish. `_closeModal` takes the next number
   * and its transition callback runs only while that number is current;
   * `_openModal` takes a number too, so a close that an open request interrupts does
   * not run its tail. Such a close sets no `open = false`, emits no `close` and
   * does not hand the host back to its parent (PSTUDIO-4804).
   */
  private closeRun = 0;

  /** Indicates whether or not the modal is open. */
  @property({ type: Boolean, reflect: true }) open? = false;

  /** The modal's size. */
  @property({ reflect: true }) size: 'medium' | 'large' = 'medium';

  // eslint-disable-next-line lit/no-native-attributes
  @property({ reflect: true }) id = `modal-${nanoid()}`;

  @property({ type: Boolean, reflect: true, attribute: 'use-portal' })
  usePortal = true;

  @property({ type: Boolean, reflect: true, attribute: 'hide-close-btn' })
  hideCloseBtn = false;

  @property({ type: Boolean, attribute: 'stay-on-click-outside' })
  stayOnClickOutside = false;

  @property({ type: Boolean, attribute: 'stay-on-esc' }) stayOnEsc = false;

  @property({ type: Boolean, attribute: 'expandable' }) expandable = false;

  constructor() {
    super();

    addons.getChannel().addListener('storyChanged', (foo) => {
      this._closeModal();
    });
  }

  firstUpdated() {
    this.onClickAway.element = this.modalBody;

    if (this.parentElement && !this.parentEl && this.usePortal) {
      // Keep the reference of parent element to append `this` instance of popover once portal is closed.
      this.parentEl = this.parentElement;
    }
  }

  triggerStateChange() {
    if (
      this.open === true &&
      this.currentState !== 'opened' &&
      this.currentState !== 'opening'
    ) {
      this._openModal();
    } else if (
      !this.open &&
      this.currentState !== 'closing' &&
      this.currentState !== 'closed'
    ) {
      this._closeModal();
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('open')) {
      this.triggerStateChange();

      this.onClickAway.excludeElement = !this.open;
    }
  }

  @state() modalId: string;

  @state() expanded: boolean = false;

  async connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();
    this.focusTrapController.hostConnected();

    this.onClickAway.callback = (event) => {
      if (!this.open || !this.hasUpdated || this.stayOnClickOutside) return;

      // @todo: consider updating to include a few other elements if needed; maybe elevate config for this to the top?
      if (event.target) {
        const target = event.target as HTMLElement;
        if (target.closest('.c-tooltip') || target.closest('.c-popover'))
          return;
      }

      this._closeModal();
    };

    if (this.parentNode === document.body) this.usePortal = false;

    if (this.usePortal) {
      this.portalDestinationEl = document.querySelector(
        this.portalDestinationSelector
      );

      if (!this.portalDestinationEl) {
        const destinationEl = document.createElement(
          PortalDestinationWC.tagname
        );

        destinationEl.setAttribute('name', this.id);
        document.body.appendChild(destinationEl);
      }
    }
  }

  disconnectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.disconnectedCallback();
    this.focusTrapController?.hostDisconnected();
    this.onClickAway.hostDisconnected();
    clearAllBodyScrollLocks();

    // auto-cleanup portal destinations when modal removed
    if (this.portalDestinationEl && this.usePortal && document.body) {
      document.body.removeChild(this.portalDestinationEl);
    }
  }

  @state() currentState: 'closed' | 'closing' | 'opened' | 'opening' = 'closed';

  // eslint-disable-next-line class-methods-use-this
  @eventOptions({ passive: true })
  _handleKeyPress(e: KeyboardEvent) {
    if (e.key === 'Escape' && !this.stayOnEsc) {
      this._closeModal();
    }
  }

  _expandModal() {
    this.expanded = !this.expanded;
  }

  render() {
    return html`
      <dialog
        class="${classMap({
          'c-modal': true,
          [`c-modal--${this.currentState}`]: true,
          'c-modal--expanded': this.expanded,
        })}"
      >
        <div
          class="c-modal__backdrop"
          @click=${() => {
            if (this.stayOnClickOutside) return;
            this._closeModal();
          }}
        ></div>

        <div
          class="${classMap({
            'c-modal__body': true,
            [`c-modal__body--size-${this.size}`]: this.size,
            'c-modal__body--expanded': this.expanded,
          })}"
          part="modal-body"
        >
          <div class="c-modal__top-bar">
            <slot name="title"></slot>
            <div class="c-modal__top-bar-actions">
              ${this.expandable
                ? html`
                    <div class="c-modal__expand-btn">
                      <ps-icon-button
                        size="small"
                        name=${this.expanded ? 'collapse' : 'expand'}
                        @click=${this._expandModal}
                      ></ps-icon-button>
                    </div>
                  `
                : ''}
              ${!this.hideCloseBtn
                ? html`
                    <div class="c-modal__close-btn">
                      <ps-icon-button
                        test-id=${this['test-id']
                          ? `${this['test-id']}-close-btn`
                          : 'ps-modal-close-btn'}
                        size="small"
                        name="close"
                        @click=${this._closeModal}
                      ></ps-icon-button>
                    </div>
                  `
                : ''}
            </div>
          </div>
          ${this.slotted('header')
            ? html`<div class="c-modal__header">
                <slot name="header"></slot>
              </div>`
            : null}

          <slot></slot>

          ${this.slotted('footer')
            ? html`<div class="c-modal__footer" part="modal-footer">
                <slot name="footer"></slot>
              </div>`
            : null}
        </div>
      </dialog>
    `;
  }

  private get portalDestinationSelector() {
    return `${PortalDestinationWC.tagname}[name="${this.id}"]`;
  }

  private _openModal() {
    // Opened again while a close is still waiting for its transition: that close must
    // not finish over this open. Without this, its tail ran whenever the transition
    // ended before the 100 ms timer below took over, and set `open = false` under a
    // dialog that was on screen. When the open timer took over first it silently
    // dropped the close instead, which is why no `close` was emitted either
    // (PSTUDIO-4804).
    this.closeRun += 1;

    if (this.usePortal) {
      setTimeout(() => {
        // Opened again while still closing: the host is already in its destination's
        // slot. Handing it over again re-inserts it into that slot, which runs
        // `disconnectedCallback` (it removes `portalDestinationEl` from the body)
        // and then `connectedCallback` (it finds no destination and builds an empty
        // one), leaving the host detached (PSTUDIO-4804). Observed live: an empty
        // destination of the same name in the body and `portalDestinationEl === null`.
        if (this.closest(this.portalDestinationSelector)) {
          return;
        }

        this.emit('update-portal-content', {
          detail: {
            destination: this.id,
            content: this,
          },
        });
      }, 50);
    }

    if (this.currentState === 'closed' || this.currentState === 'closing') {
      setTimeout(() => {
        this.currentState = 'opening';
        window.addEventListener('keydown', (e) => this._handleKeyPress(e));

        // This timer is not cancelled when the host leaves the document, so a page
        // left within these 100 ms (PSTUDIO-4555) used to reach this line for a
        // modal that was already gone. `disconnectedCallback` had released the
        // locks before the timer fired, so the lock taken here had no owner left
        // to release it; measured live, the body stayed `overflow: hidden` until
        // the next navigation. Only the lock is skipped: everything else in this
        // callback still runs for a removed host, including the `keydown` listener
        // added above, which is an anonymous function that `_closeModal` cannot
        // remove (a leak this change does not touch).
        if (this.isConnected) {
          disableBodyScroll(this.modalBody, {
            reserveScrollBarGap: true,
            allowTouchMove: (el) =>
              !!el.closest('ps-modal') && el.localName !== 'ps-modal',
          });
        }

        onceTransitionEnd(this.modalBody).then((event) => {
          this.focusTrapController.activate();

          this.currentState = 'opened';

          if (this.modal) {
            this.modal.open = true;
          }
        });
      }, 100);
    }
  }

  private _closeModal() {
    if (this.currentState === 'opening' || this.currentState === 'opened') {
      this.currentState = 'closing';

      setTimeout(() => {
        this.focusTrapController.deactivate();
        this.modal?.close();
        window.removeEventListener('keydown', this._handleKeyPress);
      }, 100);
    }

    this.closeRun += 1;
    const run = this.closeRun;

    onceTransitionEnd(this.modalBody).then((event) => {
      if (run !== this.closeRun) {
        return;
      }

      this.currentState = 'closed';
      enableBodyScroll(this.modalBody);
      this.open = false;
      this.emit('close');

      if (this.usePortal) {
        this.emit('update-portal-content', {
          detail: {
            destination: this.id,
            content: '',
          },
        });

        if (this.parentEl && !this.parentEl.contains(this)) {
          this.parentEl.append(this);
        }
      }
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-modal': ModalWC;
  }
  enum PSElementTagNameMap {
    'ps-modal' = 'ps-modal',
  }
}
